/**
 * @file: useQuotationSubjectSelection.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useCreation } from '@euler/utils/hooks';
import { useCallback, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';

export const useQuotationSubjectSelection = () => {
  const { taskManager } = useTaskContext();

  const initialSelection = useRef(
    taskManager.quotationManager.staged$.value.subjects.map(x => x.name) ?? [],
  );

  const selection$ = useCreation(
    () => new BehaviorSubject<string[]>(initialSelection.current),
    [],
  );

  const addCustomQuotationSubject = useCallback(
    (name: string) => {
      name = name.trim();
      if (!name) return;
      if (!selection$.value.includes(name)) {
        selection$.next([...selection$.value, name]);
      }
    },
    [selection$],
  );

  const removeQuotationSubject = useCallback(
    (name: string) => {
      selection$.next(selection$.getValue().filter(x => x !== name));
    },
    [selection$],
  );

  const toggleQuotationSubject = useCallback(
    (name: string) => {
      const selection = new Set(selection$.getValue());
      if (selection.has(name)) {
        selection.delete(name);
      } else {
        selection.add(name);
      }
      selection$.next([...selection]);
    },
    [selection$],
  );

  const clearSelection = useCallback(() => {
    selection$.next([]);
  }, [selection$]);

  const isDirty = useCallback(() => {
    return (
      [...selection$.value].sort().join() !==
      initialSelection.current.sort().join()
    );
  }, [selection$.value]);

  return {
    selection$,
    addCustomQuotationSubject,
    removeQuotationSubject,
    toggleQuotationSubject,
    clearSelection,
    isDirty,
  };
};
